import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, Check, Mail, MapPin } from "lucide-react";

import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Compact contact widget for sidebars on industry / product detail pages.
 * Posts to the same /api/contact endpoint, with a preset service_interest.
 */
export default function MiniContactCard({
    serviceInterest,
    eyebrow = "Talk to our team",
    heading = "Let's discuss your program.",
    description = "Drop us a note. A senior team member will get back to you within one business day.",
    testidPrefix = "mini-contact",
}) {
    const [form, setForm] = useState({ name: "", email: "", message: "" });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(false);

    const update = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const validate = () => {
        const next = {};
        if (!form.name.trim()) next.name = "Required";
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) next.email = "Valid email please";
        if (!form.message.trim() || form.message.trim().length < 10)
            next.message = "Add a short note";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Please fix the highlighted fields.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/contact`, {
                ...form,
                service_interest: serviceInterest || "Other",
            });
            setDone(true);
            toast.success("Thanks — we'll be in touch within one business day.");
            setForm({ name: "", email: "", message: "" });
        } catch (err) {
            console.error(err);
            const status = err?.response?.status;
            if (status === 429) {
                toast.error("Too many submissions from this address. Try again later.");
            } else {
                toast.error("Couldn't send your message. Please try again.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div
            data-testid={`${testidPrefix}-card`}
            className="rounded-2xl bg-white border border-[#303030]/8 p-6 sticky top-28"
        >
            <span className="pqx-eyebrow">{eyebrow}</span>
            <h3 className="pqx-display text-xl text-[#303030] mt-4">{heading}</h3>
            <p className="mt-2 text-sm text-[#303030]/65 leading-relaxed">
                {description}
            </p>

            {done ? (
                <div className="mt-6 p-5 rounded-xl bg-[#FF4040]/8 border border-[#FF4040]/20">
                    <div className="flex items-center gap-2 text-[#FF4040]">
                        <Check className="w-4 h-4" />
                        <p className="text-sm font-semibold">Message received</p>
                    </div>
                    <p className="text-xs text-[#303030]/70 mt-2">
                        We'll respond within one business day.
                    </p>
                    <button
                        type="button"
                        onClick={() => setDone(false)}
                        className="mt-4 text-xs font-semibold text-[#303030] hover:text-[#FF4040]"
                        data-testid={`${testidPrefix}-send-another`}
                    >
                        Send another →
                    </button>
                </div>
            ) : (
                <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
                    <FieldMini label="Name" error={errors.name}>
                        <Input
                            data-testid={`${testidPrefix}-name`}
                            value={form.name}
                            onChange={(e) => update("name", e.target.value)}
                            placeholder="Your name"
                            className="h-10"
                        />
                    </FieldMini>
                    <FieldMini label="Work email" error={errors.email}>
                        <Input
                            data-testid={`${testidPrefix}-email`}
                            type="email"
                            value={form.email}
                            onChange={(e) => update("email", e.target.value)}
                            placeholder="you@company.com"
                            className="h-10"
                        />
                    </FieldMini>
                    <FieldMini label={`How can we help with ${serviceInterest}?`} error={errors.message}>
                        <Textarea
                            data-testid={`${testidPrefix}-message`}
                            value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            placeholder="A few lines is enough…"
                            rows={4}
                            className="resize-none"
                        />
                    </FieldMini>
                    <button
                        type="submit"
                        data-testid={`${testidPrefix}-submit`}
                        disabled={loading}
                        className="pqx-btn-accent w-full disabled:opacity-70 disabled:cursor-not-allowed"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Sending…
                            </>
                        ) : (
                            <>
                                Send enquiry
                                <ArrowUpRight className="w-4 h-4" />
                            </>
                        )}
                    </button>
                </form>
            )}

            <div className="mt-6 pt-5 border-t border-[#303030]/8 space-y-3 text-sm text-[#303030]/70">
                <a
                    href="mailto:contact@perceptiqx.com"
                    className="flex items-center gap-2.5 hover:text-[#FF4040]"
                    data-testid={`${testidPrefix}-email-link`}
                >
                    <Mail className="w-4 h-4 text-[#FF4040]" />
                    contact@perceptiqx.com
                </a>
                <p className="flex items-center gap-2.5">
                    <MapPin className="w-4 h-4 text-[#FF4040]" />
                    Kavuri Hills, Hyderabad
                </p>
            </div>
        </div>
    );
}

function FieldMini({ label, error, children }) {
    return (
        <div>
            <Label className="text-[11px] uppercase tracking-[0.16em] text-[#303030]/65 font-semibold">
                {label}
            </Label>
            <div className="mt-1.5">{children}</div>
            {error && (
                <p className="mt-1 text-[11px] text-[#FF4040]">{error}</p>
            )}
        </div>
    );
}
